import React, { useRef, useEffect } from "react";
import { Plugin } from "prosemirror-state";
import { Note } from "../../shared/types";
import { createEditorView } from "../editor/editorView";
import { fixTopLevelTokens } from "../export/fixTokens";
import { makeNote } from "../model/defaults";
import { appNoteStore } from "../model/services";
import { sortByCreatedAt } from "../search/sortUtils";
import appLogger from "../utils/logger";
import { mobileNoteMenuPlugin } from "../editor/features/note/mobileNoteMenuPlugin";
import { PublicNoteMenu } from "./PublicNoteMenu";

const logger = appLogger.with({ namespace: "public/editor" });

const createMarkupPrivateReferencesPlugin = (privateNoteIds: Set<string>) => {
  logger.info("Creating markup private references plugin");
  return new Plugin({
    appendTransaction: (trs, oldState, newState) => {
      logger.info("Markup private references");
      const tr = newState.tr;
      newState.doc.descendants((node, pos) => {
        if (node.type.name === "reference" && !node.attrs.isPrivate && privateNoteIds.has(node.attrs.linkedNoteId)) {
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, isPrivate: true });
        }
      });
      return tr.docChanged ? tr : null;
    },
  });
};

export const PublicEditor = ({
  publicNotes,
  linkedPrivatePreviews,
  visibleNotes,
}: {
  publicNotes: Note[];
  linkedPrivatePreviews: { id: string; text: string }[];
  visibleNotes?: string[];
}) => {
  const editorDiv = useRef<HTMLDivElement>(null);

  // Render the editor once the notes are loaded without error
  useEffect(() => {
    if (!editorDiv.current) return;
    // Populate the note list with the public note and mock notes for the linked note previews
    const mockPrivateNotes = linkedPrivatePreviews.map((n) => makeNote({ id: n.id, tokens: [n.text], readAll: false }));
    appNoteStore.upsert(
      [...publicNotes, ...mockPrivateNotes].map((n) => ({ ...n, tokens: fixTopLevelTokens(n.tokens) })),
      false,
    );

    logger.info("Creating public editor");
    // We want an editor which users can't edit, but we want to be able to toggle references (which
    // is done by editing the note node). The set up to get this is a bit weird, but is done this way
    // to minimize modifying existing code. Comments below explain the weirdness. If you come across
    // this and have some time, please make it better!
    const privateNoteIds = new Set(mockPrivateNotes.map((n) => n.id));
    const visibleNotesSet = visibleNotes ? new Set(visibleNotes) : null;
    const editor = createEditorView({
      // We set isReadOnly to false, because otherwise we get an error every
      // time we toggle a reference (I think cause createEditorView sets
      // editable to false, when isReadOnly is true).
      isReadOnly: false,
      div: editorDiv.current,
      initialNotes: sortByCreatedAt(
        publicNotes
          .filter((n) => (visibleNotesSet ? visibleNotesSet.has(n.id) : true))
          .map((n) => ({ entry: appNoteStore.get(n.id)! })),
      ),
    });
    // When we set isReadOnly to false, all the usual plugins are loaded,
    // including the persistence plugin, which could easily lead to accidental
    // persistence of the public notes. This replaces all the plugins with a
    // new set.
    const reconfiguredState = editor.view.state.reconfigure({
      // This plugin marks references to private notes as private
      plugins: [createMarkupPrivateReferencesPlugin(privateNoteIds), mobileNoteMenuPlugin],
    });
    editor.view.updateState(reconfiguredState);
    // We set contenteditable to false to prevent the user from editing the doc
    editorDiv.current.contentEditable = "false";
    // Force a transaction to mark private references
    editor.view.dispatch(editor.view.state.tr);
  }, [publicNotes, linkedPrivatePreviews, visibleNotes]);

  return (
    <>
      <PublicNoteMenu />
      <div ref={editorDiv} className="editor-div ProseMirror public-editor" style={{ padding: `24px 24px` }} />
    </>
  );
};
